import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
  useRouteLoaderData,
} from 'react-router'

import { BackHomeLink, StatusBlock, TryAgainButton } from './components/status-screen'
import { StructuredData } from './components/structured-data'
import './app.css'
import type { Route } from './+types/root'
// The two font files used above the fold, imported so the preload href is the SAME
// fingerprinted URL app.css resolves to (VIT-17753). A hardcoded path would miss the hash
// and fetch the font twice. Both are single variable faces covering their whole weight
// range, so one preload each fully covers the display and body roles above the fold —
// see the @font-face blocks in app.css.
import spaceGroteskUrl from './fonts/space-grotesk.woff2'
import workSansUrl from './fonts/work-sans.woff2'
// The DARK wordmark for the Organization logo: a consumer that renders it (a
// Google knowledge panel, a chat citation) puts it on a light surface, where the
// light variant's white lettering would be invisible.
import wordmarkDarkUrl from './images/wordmark-dark.svg'
import { stageRobotsDirective } from './robots'
import { serverTimingMiddleware } from './server-timing.server'
import { absoluteUrl } from './site'
import { siteGraph } from './structured-data'
import { usePageView } from './use-page-view'

// Server-only route export, so React Router strips it (and its server-only
// import above) from the client bundle. Registered on the root route so it wraps
// every SSR response — documents, `.data` payloads and actions — and stamps the
// `Server-Timing` header the read-only instrumentation API can't (VIT-17752).
export const middleware: Route.MiddlewareFunction[] = [serverTimingMiddleware]

/**
 * Carries the deploy version (the production git SHA) into the document.
 *
 * Another server-only export, stripped from the client bundle by the same mechanism as
 * `middleware` above — which is the point. Reading `import.meta.env.VITE_VERSION` in client
 * code bakes the SHA into a content-hashed chunk, renaming every bundle on every commit and
 * making returning visitors re-download assets that are served `immutable` for a year.
 *
 * `process.env.VITE_VERSION` is on the SSR Lambda via the component's `environment`, and is
 * set by the build env for the prerendered routes.
 *
 * No `shouldRevalidate` opt-out: root has no params, so React Router already skips this
 * loader on a plain navigation. Opting out would only suppress the post-submission
 * revalidation — for the whole root route, not just this value — which is a trap for
 * whoever adds real data here next.
 */
export function loader() {
  return { appVersion: process.env['VITE_VERSION'] ?? null }
}

// The default title, as a root route `meta` export rather than a <title> in
// `Layout` below: `<Meta />` renders only the deepest matched route's `meta`, so a
// page with its own title replaces this one outright. A hardcoded <title> instead
// ships *two* in the SSR document, and a crawler reading the raw HTML takes the
// first — only the hydrated DOM promoted the page's own title to the tab.
//
// So this is what an unmatched URL or a root-level error shows (the routes that
// render without their own meta), and it matches the home page title (VIT-17801).
//
// No description here on purpose: a default one would describe the product on
// pages that are about something else, so each page template declares its own.
export const meta: Route.MetaFunction = () => [{ title: 'Find the right doctor | Vital Care Finder' }]

/**
 * Keeps a preview stage out of the index (VIT-17997). `undefined` on production.
 *
 * Read at module scope from `import.meta.env` rather than `process.env`, which is the client-side
 * form (app/analytics.ts does the same) — `Layout` renders on both sides, and Vite inlines the
 * value into the SSR and client bundles from one build env, so the two cannot disagree and
 * hydration sees no mismatch. Baking a stage NAME into a hashed client chunk is safe in a way
 * `VITE_VERSION` is not (see the `loader` comment above, and DC-2089): a stage name is stable
 * across commits, so no chunk is renamed by it.
 *
 * Destructured, not `import.meta.env['VITE_ENVIRONMENT']`, and that is not a style choice — it is
 * the DC-2090 trap recorded on `getAppVersion` in app/instrumentation.client.ts. Vite emits three
 * different things here: dot access becomes the bare string; DESTRUCTURING becomes an object
 * literal narrowed to the named keys; a computed lookup gives up and inlines the WHOLE env object,
 * `VITE_VERSION` — the git SHA, put there by the `environment` block in sst.config.ts — included.
 * Since root is in every client chunk's graph, that last form renames every fingerprinted bundle
 * on every deploy, and they are served `immutable` for a year. Dot access is the other safe form
 * but trips TS4111 against the index signature on `ImportMetaEnv`; destructuring does not.
 */
const { VITE_ENVIRONMENT } = import.meta.env
const robotsDirective = stageRobotsDirective(VITE_ENVIRONMENT)

export function Layout({ children }: { children: React.ReactNode }) {
  // Optional: `Layout` also wraps the `ErrorBoundary` render, where root loader data can be absent.
  const rootData = useRouteLoaderData<typeof loader>('root')

  return (
    <html lang="en" dir="ltr">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        <link rel="icon" href="/favicon.svg" type="image/svg+xml" />
        <link rel="icon" href="/favicon.ico" sizes="32x32" />
        <link rel="apple-touch-icon" href="/apple-touch-icon.png" />

        {/* Without these the fonts are discovered only after the stylesheet has been
            fetched and parsed, so `font-display: swap` shows the metric-adjusted local
            fallback for an extra round trip. `crossOrigin` is required even same-origin:
            fonts are fetched in CORS mode, and a preload that doesn't match the mode of
            the real request is ignored and the file downloaded twice. */}
        <link rel="preload" as="font" type="font/woff2" href={workSansUrl} crossOrigin="anonymous" />
        <link rel="preload" as="font" type="font/woff2" href={spaceGroteskUrl} crossOrigin="anonymous" />

        {/* Off-production only (VIT-17997). This is the ONLY directive that reaches the
            prerendered pages (/terms, /privacy, /support, /mcp-apps, /mcp-demo) on the inner
            `sst.aws.React` distribution — the surface `PreviewUrl` advertises: those are static
            S3 objects that never invoke the server Lambda, so entry.server.tsx cannot stamp
            them, and that distribution has no CloudFront response headers policy attached.

            In `Layout` rather than root's `meta` export for the reason recorded on the `meta`
            comment above: `<Meta />` renders only the deepest matched route's descriptors, and
            every route below root declares its own — so a robots descriptor there would never
            reach the document on the pages that most need it.

            /mcp-demo therefore carries this alongside its own identical `noindex, nofollow` on a
            preview. Duplicated rather than contradictory, and Google takes the most restrictive
            of repeated directives, so it is left to be redundant instead of special-cased. */}
        {robotsDirective ? <meta name="robots" content={robotsDirective} /> : null}

        {/* Read via `app-version.ts`. Rendered from the root loader so the SHA stays out of the
            hashed client chunks — see the comment on `loader` above. Rendered on both server and
            client, so hydration sees no mismatch. */}
        {rootData?.appVersion ? <meta name="app-version" content={rootData.appVersion} /> : null}

        {/* The site-wide WebSite + Organization graph, on every page (VIT-17746).
            This is what makes Google render "Vital Care Finder" above the URL in a
            SERP snippet instead of the bare `vit.al` hostname it falls back to
            with no structured data at all.

            Rendered here rather than from root's `meta` export because `<Meta />`
            renders only the deepest matched route's `meta`, and every route below
            has one of its own — so a node declared there would never reach the
            document. Same trap as the `<title>` the `meta` comment above records.

            Per-route graphs (BreadcrumbList, Physician, ItemList) do go through
            `meta`, as `script:ld+json` descriptors. */}
        <StructuredData data={siteGraph(absoluteUrl(wordmarkDarkUrl))} />

        <Meta />
        <Links />
      </head>
      <body className="antialiased">
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}

export default function App() {
  // GA4 page views for client-side navigations (VIT-17882). Mounted here, above every
  // route, so one hook covers the whole app. No-op outside production.
  usePageView()

  return <Outlet />
}

// The root boundary is the last line of defence: it catches an unmatched URL
// (a 404 that never reached a route with its own boundary) and any error thrown
// above the page layout — so unlike the results/provider boundaries it renders
// with no shared header or footer, and owns its own full-height centring.
export function ErrorBoundary() {
  const error = useRouteError()

  // A 404 here is an unknown URL — nothing to retry, just point home. Anything
  // else is unexpected and might be transient, so offer a retry too. The raw
  // error is intentionally not shown: it's captured for Datadog (see
  // entry.server.tsx), and a status code or stack means nothing to a visitor.
  const isNotFound = isRouteErrorResponse(error) && error.status === 404

  return (
    <main className="grid min-h-dvh place-items-center px-3 py-16 sm:px-4" data-testid="error-boundary">
      {isNotFound ? (
        <StatusBlock
          variant="not-found"
          title="Page not found"
          description="The page you're looking for doesn't exist or may have moved."
        >
          <BackHomeLink />
        </StatusBlock>
      ) : (
        <StatusBlock
          variant="error"
          title="Something went wrong"
          description="We hit an unexpected problem — it's on us, not you. Please try again in a moment."
        >
          <TryAgainButton />
          <BackHomeLink />
        </StatusBlock>
      )}
    </main>
  )
}
